import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight, Minus, type LucideIcon } from 'lucide-react';
import { formatMoney, type DashboardSoFar } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { cn, display } from '@/lib/utils';
import { useReducedMotion } from '@/lib/motion';
import { changePct } from './dashboard-math';

export type Tone = 'primary' | 'sky' | 'violet' | 'amber' | 'rose' | 'green';

export const TONE_COLOR: Record<Tone, string> = {
  primary: 'hsl(var(--primary))',
  sky: 'hsl(var(--chart-2))',
  violet: 'hsl(var(--chart-3))',
  amber: 'hsl(var(--chart-4))',
  rose: 'hsl(var(--chart-5))',
  green: 'hsl(var(--chart-6))',
};

const TONE_BADGE: Record<Tone, string> = {
  primary: 'bg-primary/10 text-primary',
  sky: 'bg-chart-2/10 text-chart-2',
  violet: 'bg-chart-3/10 text-chart-3',
  amber: 'bg-chart-4/10 text-chart-4',
  rose: 'bg-chart-5/10 text-chart-5',
  green: 'bg-chart-6/10 text-chart-6',
};

const TONE_GLOW: Record<Tone, string> = {
  primary: 'bg-primary',
  sky: 'bg-chart-2',
  violet: 'bg-chart-3',
  amber: 'bg-chart-4',
  rose: 'bg-chart-5',
  green: 'bg-chart-6',
};

export function moneyWhole(minor: number): string {
  return formatMoney(minor, display.currency, display.locale, 0);
}

export function formatCount(value: number): string {
  return new Intl.NumberFormat(display.locale, { maximumFractionDigits: 0 }).format(
    Math.round(value),
  );
}

export function AnimatedNumber({
  value,
  format = formatCount,
  durationMs = 900,
}: {
  value: number;
  format?: (value: number) => string;
  durationMs?: number;
}) {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(reduced ? value : 0);
  const from = useRef(reduced ? value : 0);

  useEffect(() => {
    if (reduced) {
      from.current = value;
      setShown(value);
      return;
    }
    const start = from.current;
    const began = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - began) / durationMs);
      const current = start + (value - start) * (1 - Math.pow(1 - progress, 3));
      from.current = current;
      setShown(current);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, reduced, durationMs]);

  return <span className="tabular-nums">{format(shown)}</span>;
}

export function IconBadge({
  icon: Icon,
  tone = 'primary',
  className,
}: {
  icon: LucideIcon;
  tone?: Tone;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'grid h-9 w-9 shrink-0 place-items-center rounded-lg',
        TONE_BADGE[tone],
        className,
      )}
    >
      <Icon className="h-[18px] w-[18px]" aria-hidden />
    </span>
  );
}

export function Panel({
  title,
  subtitle,
  icon,
  tone = 'primary',
  action,
  children,
  className,
  index = 0,
}: {
  title: string;
  subtitle?: ReactNode;
  icon?: LucideIcon;
  tone?: Tone;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  index?: number;
}) {
  return (
    <section
      className={cn(
        'flex min-w-0 flex-col rounded-xl border bg-card p-4 shadow-sm transition-shadow hover:shadow-md sm:p-5',
        'duration-500 animate-in fade-in-0 slide-in-from-bottom-3 fill-mode-backwards',
        className,
      )}
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <header className="mb-4 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          {icon && <IconBadge icon={icon} tone={tone} />}
          <div className="min-w-0">
            <h2 className="text-sm font-semibold leading-tight">{title}</h2>
            {subtitle && <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
        {action}
      </header>
      <div className="flex min-w-0 flex-1 flex-col">{children}</div>
    </section>
  );
}

export function PanelLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="shrink-0 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10 focus-ring"
    >
      {children}
    </Link>
  );
}

export function DeltaBadge({
  soFar,
  upIsGood = true,
}: {
  soFar: DashboardSoFar;
  upIsGood?: boolean;
}) {
  const change = changePct(soFar);
  if (change === null) return null;
  const flat = Math.abs(change) < 1;
  const good = flat ? null : change > 0 === upIsGood;
  const Icon = flat ? Minus : change > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
        good === null && 'bg-muted text-muted-foreground',
        good === true && 'bg-success/10 text-success',
        good === false && 'bg-destructive/10 text-destructive',
      )}
      title={tr('admin', 'dashboard_compared_last_week')}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {flat ? '0%' : `${Math.abs(change)}%`}
    </span>
  );
}

export function KpiCard({
  label,
  icon,
  tone,
  value,
  format,
  soFar,
  caption,
  chart,
  to,
  index = 0,
}: {
  label: string;
  icon: LucideIcon;
  tone: Tone;
  value: number;
  format?: (value: number) => string;
  soFar?: DashboardSoFar;
  caption?: ReactNode;
  chart?: ReactNode;
  to?: string;
  index?: number;
}) {
  const body = (
    <>
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute -end-10 -top-10 h-28 w-28 rounded-full opacity-[0.12] blur-2xl',
          TONE_GLOW[tone],
        )}
      />
      <div className="flex min-w-0 items-center gap-2.5">
        <IconBadge icon={icon} tone={tone} className="h-8 w-8 sm:h-9 sm:w-9" />
        <p className="line-clamp-2 text-xs font-medium leading-tight text-muted-foreground sm:text-sm">
          {label}
        </p>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
        <p className="text-xl font-semibold tracking-tight sm:text-2xl xl:text-[1.7rem]">
          <AnimatedNumber value={value} format={format} />
        </p>
        {soFar && <DeltaBadge soFar={soFar} />}
      </div>
      {caption && <p className="mt-0.5 truncate text-xs text-muted-foreground">{caption}</p>}
      {chart && <div className="-mx-1 mt-auto h-12 pt-3">{chart}</div>}
    </>
  );

  const className = cn(
    'relative flex h-full min-w-0 flex-col overflow-hidden rounded-xl border bg-card p-3.5 shadow-sm sm:p-4 transition-all hover:-translate-y-0.5 hover:shadow-md',
    'duration-500 animate-in fade-in-0 slide-in-from-bottom-3 fill-mode-backwards',
  );
  const style = { animationDelay: `${index * 70}ms` };

  return to ? (
    <Link to={to} className={cn(className, 'focus-ring')} style={style}>
      {body}
    </Link>
  ) : (
    <div className={className} style={style}>
      {body}
    </div>
  );
}

export interface TooltipItem {
  name?: string | number;
  value?: number | string | (number | string)[];
  color?: string;
  dataKey?: string | number | ((row: unknown) => unknown);
}

export function ChartTooltip({
  active,
  payload,
  label,
  labelFormatter,
  valueFormatter,
}: {
  active?: boolean;
  payload?: TooltipItem[];
  label?: string | number;
  labelFormatter?: (label: string | number) => ReactNode;
  valueFormatter?: (value: number, item: TooltipItem) => ReactNode;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="min-w-[10rem] rounded-lg border bg-popover/95 px-3 py-2 text-xs text-popover-foreground shadow-lg backdrop-blur">
      {label !== undefined && (
        <p className="mb-1.5 font-medium">{labelFormatter ? labelFormatter(label) : label}</p>
      )}
      <ul className="space-y-1">
        {payload.map((item) => (
          <li
            key={String(item.dataKey ?? item.name)}
            className="flex items-center justify-between gap-4"
          >
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: item.color }}
                aria-hidden
              />
              {item.name}
            </span>
            <span className="font-semibold tabular-nums">
              {valueFormatter
                ? valueFormatter(Number(item.value ?? 0), item)
                : formatCount(Number(item.value ?? 0))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function useMounted(): boolean {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return mounted;
}

export interface BarRow {
  label: string;
  value: number;
  hint?: ReactNode;
}

export function BarList({
  rows,
  format = formatCount,
  tone = 'primary',
  empty,
}: {
  rows: BarRow[];
  format?: (value: number) => string;
  tone?: Tone;
  empty: ReactNode;
}) {
  const mounted = useMounted();
  if (rows.length === 0) return <EmptyChart>{empty}</EmptyChart>;
  const max = Math.max(...rows.map((row) => row.value), 1);
  return (
    <ul className="space-y-3">
      {rows.map((row, index) => (
        <li key={row.label}>
          <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate" title={row.label}>
              {row.label}
              {row.hint && <span className="ms-1.5 text-xs text-muted-foreground">{row.hint}</span>}
            </span>
            <span className="shrink-0 font-medium tabular-nums">{format(row.value)}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full transition-[width] duration-700 ease-out"
              style={{
                width: mounted ? `${Math.max(2, (row.value / max) * 100)}%` : '0%',
                background: TONE_COLOR[tone],
                opacity: 1 - index * 0.08,
                transitionDelay: `${index * 60}ms`,
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function EmptyChart({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'grid h-full min-h-[8rem] place-items-center rounded-lg border border-dashed px-4 text-center text-sm text-muted-foreground',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function LegendDot({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value?: ReactNode;
}) {
  return (
    <span className="flex items-center justify-between gap-3 text-xs">
      <span className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ background: color }}
          aria-hidden
        />
        <span className="leading-tight">{label}</span>
      </span>
      {value !== undefined && <span className="shrink-0 font-semibold tabular-nums">{value}</span>}
    </span>
  );
}
